import React, { useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import SwitchWrapper from "../../SwitchWrapper";
import SelectWrapper from "../../SelectWrapper";
import { IMediaPropsDetails } from "./MiddleSection";
import { EMediaType } from "../../../../../types/campaign.types";
import { ToastContext } from "../../../../../utils/context/toastContext";
import DeleteIcon2 from "../../../../../constants/icons/DeleteIcon2";
import ImageIcon from "../../../../../constants/icons/ImageIcon";
import DocumentIcon from "../../../../../constants/icons/DocumentIcon";
import { TooltipContent } from "../../../../../common/components/Tooltip";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "./FileUploadAccordions";
import RippleEffect from "./RippleEffect";
import ErrorIcon from "../../../../../constants/icons/ErrorIcon";

interface IProps extends IMediaPropsDetails {}

const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1 MB
const MAX_DOCUMENT_SIZE = 1 * 1024 * 1024; // 1 MB

const errorMsgs = {
  fileLimit: "Oops! Your file is too large. Maximum allowed size: 1 MB.",
  unsupportedImageFile:
    "Unsupported file type, please upload PNG and JPEG files only.",
  unsupportedDocumentFile:
    "Unsupported file type, please upload PDF files only.",
  default: "Something went wrong with the file upload, try again.",
};

const successMsgs = {
  fileUpload: "File uploaded successfully.",
};
const tooltipContent = {
  delete: "Remove uploaded file to change the media type.",
};

// const docsInput =
//   ".pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const docsInput = ".pdf,application/pdf";
const imageInput = "image/jpeg, image/png";

const isValidFileExtension = (file: File, allowedExtensions: string[]) => {
  const fileExtension = file?.name?.split(".")?.pop()?.toLowerCase();
  if (fileExtension) {
    return allowedExtensions.includes(fileExtension);
  }

  return false;
};

const isValidFileType = (file: File): boolean => {
  // const validTypes = [
  //   ".pdf",
  //   "application/pdf",
  //   ".docx",
  //   "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // ];

  const validTypes = [
    ".pdf",
    "application/pdf"
  ];

  // Check if the file extension or MIME type is in the validTypes array
  return (
    validTypes.includes(file.type) ||
    validTypes.some((type) => file.name.endsWith(type))
  );
};

const FileUpload: React.FC<IProps> = ({
  handleUpdateMediaDetails,
  addMediaFlag,
  mediaType,
  uploadedFile,
  fileName,
  fileSize,
  error,
  handleErrors,
}) => {
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState<boolean>(false);
  const { setToastValue } = useContext(ToastContext);

  const { getRootProps, getInputProps } = useDropzone({
    // accept:
    //   mediaType === "image"
    //     ? { [imageInput]: [] }
    //     : {
    //         [docsInput]: [],
    //       },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0]);
      }
    },
  });

  const toggleAddMedia = () => {
    handleUpdateMediaDetails({
      addMediaFlag: !addMediaFlag,
      mediaType: EMediaType.IMAGE,
      uploadedFile: null,
      mediaFileName: "",
      mediaFileSize: "",
    });
    handleErrors({ addMedia: "" });
  };

  const handleDeleteMedia = () => {
    handleUpdateMediaDetails({
      addMediaFlag,
      mediaType,
      uploadedFile: null,
      mediaFileName: "",
      mediaFileSize: "",
    });
  };

  const handleChangeMediaType = (newValue: string) => {
    handleUpdateMediaDetails({
      addMediaFlag,
      mediaType: newValue as EMediaType,
      uploadedFile: null,
      mediaFileName: "",
      mediaFileSize: "",
    });
  };

  const handleSelectClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setShowDeleteTooltip(true);
  };

  const formatFileSize = (fileSizeInBytes: number) => {
    if (fileSizeInBytes < 1024 * 1024) {
      // Calculate size in KB
      const sizeInKB = fileSizeInBytes / 1024;
      return `${sizeInKB % 1 === 0 ? sizeInKB : sizeInKB.toFixed(1)} KB`;
    } else {
      // Calculate size in MB
      const sizeInMB = fileSizeInBytes / (1024 * 1024);
      return `${sizeInMB % 1 === 0 ? sizeInMB : sizeInMB.toFixed(1)} MB`;
    }
  };

  const handleFileUpload = (file?: File) => {
    if (file) {
      setIsFileUploading(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);

      const fileSizeInBytes = file.size || 0;

      // Check for image size limit
      if (mediaType === "image") {
        if (fileSizeInBytes > MAX_IMAGE_SIZE) {
          setToastValue({
            isToastShown: true,
            toastMessage: errorMsgs.fileLimit,
            toastKey: "uploadImageError",
            toastSeverity: "error",
          });
          return;
        }

        if (!isValidFileExtension(file, ["jpeg", "jpg", "png"])) {
          setToastValue({
            isToastShown: true,
            toastMessage: errorMsgs.unsupportedImageFile,
            toastKey: "uploadImageError",
            toastSeverity: "error",
          });
          return;
        }
      }

      // Check for document size limit
      if (mediaType === "document") {
        if (fileSizeInBytes > MAX_DOCUMENT_SIZE) {
          setToastValue({
            isToastShown: true,
            toastMessage: errorMsgs.fileLimit,
            toastKey: "uploadDocError",
            toastSeverity: "error",
          });
          return;
        }

        if (!isValidFileType(file)) {
          setToastValue({
            isToastShown: true,
            toastMessage: errorMsgs.unsupportedDocumentFile,
            toastKey: "uploadDocError",
            toastSeverity: "error",
          });
          return;
        }
      }

      reader.onload = () => {
        // const base64 = reader.result as string;
        handleUpdateMediaDetails({
          addMediaFlag,
          mediaType,
          uploadedFile: file,
          mediaFileName: file.name || "",
          mediaFileSize: formatFileSize(fileSizeInBytes),
        });

        setToastValue({
          isToastShown: true,
          toastMessage: successMsgs.fileUpload,
          toastKey: "fileUploadSuccess",
          toastSeverity: "success",
        });
        setIsFileUploading(false);
        handleErrors({ addMedia: "" });
      };
      reader.onerror = (error) => {
        setToastValue({
          isToastShown: true,
          toastMessage: errorMsgs.default,
          toastKey: "uploadImageError",
          toastSeverity: "error",
        });
        setIsFileUploading(false);
        console.error("Error reading file:", error);
      };
    }
  };

  const renderUploadSection = (selectedOption: EMediaType) => {
    switch (selectedOption) {
      case "document":
        return (
          <Box>
            <Typography variant="h6">
              <Typography
                fontWeight={700}
                variant="h6"
                component="span"
              >
                Drag and drop
              </Typography>{" "}
              or{" "}
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: COLOR_MAP.DARK_PURPLE,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationThickness: "1.20px",
                }}
                component="span"
              >
                Browse
                <input
                  hidden
                  type="file"
                  // accept={docsInput}
                  onChange={(e) => handleFileUpload(e?.target?.files?.[0])}
                />
              </Typography>{" "}
              to upload a Job Description
            </Typography>
            <Typography variant="h6" sx={{ color: COLOR_MAP.GREY_3 }}>
              {/* (Formats: PDF, DOCX | Max Size: 1 MB) */}
              (Format: <b>PDF</b> | Max Size: <b>1 MB</b>)
            </Typography>
          </Box>
        );
      default:
        // Default Image
        return (
          <Box>
            <Typography variant="h6">
              <Typography
                fontWeight={700}
                variant="h6"
                component="span"
              >
                Drag and drop
              </Typography>{" "}
              or{" "}
              <Typography
                variant="h6"
                fontWeight={600}
                component="span"
                sx={{
                  color: COLOR_MAP.DARK_PURPLE,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationThickness: "1.20px",
                }}
              >
                Browse
                <input
                  hidden
                  type="file"
                  // accept={imageInput}
                  onChange={(e) => handleFileUpload(e?.target?.files?.[0])}
                />
              </Typography>{" "}
              to upload your brand logo or a banner
            </Typography>
            <Typography variant="h6" sx={{ color: COLOR_MAP.GREY_3 }}>
              (Formats: <b>JPEG, PNG</b> | Max Size: <b>1 MB</b>)
            </Typography>
          </Box>
        );
    }
  };

  const renderUploadFile = () => {
    // if (isFileUploading) {
    //   return (
    //     <>
    //       <Box sx={{ display: "flex", justifyContent: "space-between" , padding:1.75}}>
    //         <Typography variant="h6">Uploading...</Typography>
    //         <IconButton disableRipple> {DELETE_ICON}</IconButton>
    //       </Box>
    //       <LinearProgress variant="determinate" sx={{ color: "#2BA682" }} value={100} />
    //     </>
    //   );
    // }

    if (uploadedFile) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {fileName}
            </Typography>
            <Typography variant="h6">Size: {fileSize}</Typography>
          </Box>

          <Tooltip
            title={
              <Box sx={{ maxWidth: 250 }}>
                <TooltipContent title={tooltipContent.delete} />
              </Box>
            }
            arrow
            placement="top-end"
            open={showDeleteTooltip}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [15, 0],
                  },
                },
              ],
            }}
          >
            <IconButton
              sx={{ position: "relative" }}
              disableRipple
              onClick={handleDeleteMedia}
            >
              <RippleEffect isRippleVisible={showDeleteTooltip} />
              <DeleteIcon2 />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <>
        <input {...getInputProps()} />
        {renderUploadSection(mediaType)}
      </>
    );
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showDeleteTooltip) {
      timer = setTimeout(() => {
        setShowDeleteTooltip(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showDeleteTooltip]);

  return (
    <Box sx={{ backgroundColor: COLOR_MAP.WHITE_BACKGROUND }}>
      <Accordion
        expanded={addMediaFlag}
        sx={{
          boxShadow: "none",
          p: 0,
        }}
      >
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          sx={{ backgroundColor: COLOR_MAP.LIGHT_GREY2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "fontWeightBold", mr: 1 }}
            >
              Add media file{" "}
              <Typography
                component="span"
                sx={{ color: COLOR_MAP.GREY_3, fontSize: 13 }}
              >
                (Image or document)
              </Typography>
            </Typography>

            <SwitchWrapper checked={addMediaFlag} onChange={toggleAddMedia} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Upload media section  */}
          <Box display="flex" alignItems="start" gap={1}>
            {/* Select media type */}
            <SelectWrapper
              value={mediaType}
              handleChange={handleChangeMediaType}
              disabled={!!uploadedFile}
              handleClick={!!uploadedFile ? handleSelectClick : undefined}
              menuItems={[
                {
                  label: "Image",
                  value: "image",
                  Icon: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      <ImageIcon height={18} width={18} />
                    </Box>
                  ),
                },
                {
                  label: "Document",
                  value: "document",
                  Icon: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      <DocumentIcon height={18} width={16} />
                    </Box>
                  ),
                },
              ]}
              renderValue={(value) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {value === "image" && <ImageIcon height={24} width={22} />}
                    {value === "document" && (
                      <DocumentIcon height={24} width={22} />
                    )}
                    <Typography
                      variant="h6"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                width: 120,
                backgroundColor: COLOR_MAP.LIGHT_PURPLE2,
                height: "100%",
                borderRadius: "8px",
              }}
            />

            {/* Dynamic Upload Section */}
            <Box flex={1}>
              <Box
                border={`1px dashed ${error ? COLOR_MAP.ERROR : "#ccc"}`}
                borderRadius={2}
                padding={1.75}
                {...getRootProps()}
                sx={{ cursor: "pointer" }}
              >
                {renderUploadFile()}
              </Box>
              {!!error && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <ErrorIcon />
                  <Typography
                    sx={{ ml: 1, color: COLOR_MAP.ERROR }}
                    variant="h6"
                  >
                    {error}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default React.memo(FileUpload);
